import { TimePreference, LBBContact, MunchedImage } from 'sb-lib/types';
import { Client } from './client';
import { OrgIntegration, OtherUserIntegration, UserIntegration } from './integration';
import { Plan } from './payment';
import { VideoEffect } from 'sb-lib/webrtc/client';

export enum OrgRole {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
  VIEWER = 'VIEWER',
  CUSTOM = 'CUSTOM',
}

export enum OrgStatus {
  SETTING_UP = 'SETTING_UP',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DELETED = 'DELETED',
}

export enum OnboardingStep {
  UPLOAD_AVATAR = 'UPLOAD_AVATAR',
  BROWSER_ALERTS = 'BROWSER_ALERTS',
  CONFIGURE_MEDIA = 'CONFIGURE_MEDIA',
  CONNECT_SESSION = 'CONNECT_SESSION',
  CONFIGURE_SECURITY = 'CONFIGURE_SECURITY',
  CONFIGURE_HOURS = 'CONFIGURE_HOURS',
}

export interface Domain {
  id: number;
  url: string;
  dateCreated: number;
  verified: boolean;
}

export enum HideCondition {
  OFF_HOURS = 'off-hours',
  NONE_ONLINE = 'none-online',
  BUSY = 'busy',
}

export enum WidgetSettingsCopyKey {
  IDLE_HELP_TEXT = 'IDLE_HELP_TEXT',
  IDLE_HELP_TEXT_FOCUSED = 'IDLE_HELP_TEXT_FOCUSED',
  IDLE_BUSY_TEXT = 'IDLE_BUSY_TEXT',
  IDLE_BUSY_TEXT_FOCUSED = 'IDLE_BUSY_TEXT_FOCUSED',
  IDLE_AWAY_TEXT = 'IDLE_AWAY_TEXT',
  IDLE_AWAY_TEXT_FOCUSED = 'IDLE_AWAY_TEXT_FOCUSED',
  ANSWER_CALL = 'ANSWER_CALL',
  NEED_HELP_TITLE = 'NEED_HELP_TITLE',
  NEED_HELP_TEXT = 'NEED_HELP_TEXT',
  NEED_HELP_TITLE_OFFLINE = 'NEED_HELP_TITLE_OFFLINE',
  NEED_HELP_TEXT_OFFLINE = 'NEED_HELP_TEXT_OFFLINE',
  NEED_HELP_AWAY_TEXT = 'NEED_HELP_AWAY_TEXT',
  NEED_HELP_AWAY_TITLE = 'NEED_HELP_AWAY_TITLE',
  NEED_HELP_BUSY_TEXT = 'NEED_HELP_BUSY_TEXT',
  NEED_HELP_BUSY_TITLE = 'NEED_HELP_BUSY_TITLE',
  NEED_HELP_CTA = 'NEED_HELP_CTA',
  DIALING_TITLE = 'DIALING_TITLE',
  RECORDING_OPTIN_TITLE = 'RECORDING_OPTIN_TITLE',
  RECORDING_OPTIN_TEXT = 'RECORDING_OPTIN_TEXT',
}

export enum WidgetButtonType {
  CHAT = 'chat',
  CALL = 'call',
  MEETING = 'meeting',
}
export interface WidgetSettingsOrgButtons {
  elements: {
    name: WidgetButtonType;
    enabled: boolean;
  }[];
  fullWidth: boolean;
}

export enum WidgetSectionType {
  LIVE_AGENTS = 'live-agents',
  HELP_CENTER = 'help-center',
}

export interface WidgetSettingsOrgSections {
  elements: {
    name: WidgetSectionType;
    enabled: boolean;
  }[];
}

export interface WidgetSettingsLink {
  url: string;
  title: string;
  icon: string;
}

export enum WidgetSettingsRequireEmailCondition {
  AWAY = 'away',
  ALWAYS = 'always',
  NEVER = 'never',
}

export enum WidgetSettingsReplyTimes {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAY = 'day',
  DYNAMIC = 'dynamic',
}

export enum ConversationAutoCloseSetting {
  NEVER = 'NEVER',
  AFTER_30_MINUTES = 'AFTER_30_MINUTES',
  AFTER_60_MINUTES = 'AFTER_60_MINUTES',
}

export interface OrgWidgetSettings {
  launcher: 'pill' | 'video' | 'ai';
  hidden: boolean;
  hideConditions: HideCondition[];
  position: 'left' | 'right';
  viewing: 'ask' | 'no-ask';
  answer: 'ask' | 'no-ask';
  takeover: 'ask' | 'no-ask';
  visitorVideo: 'ask' | 'no-ask';
  branding: 'default' | 'none';
  color: null | string;
  bgColor: null | string;
  orgLogoUrl: null | string;
  orgProfileLogoUrl: null | string;
  launcherVideoUrl?: null | string;
  launcherImageUrl?: null | string;
  enabledCountries: string[];
  assetLinking: 'inline' | 'hotlink' | 'proxy';
  copy: Record<WidgetSettingsCopyKey, string | null | undefined>;
  showBookingAlways: boolean;
  showChatAlways: boolean;
  disableMobile: boolean;
  hasAvatar: boolean;
  hasDarkText: boolean;
  hasLogo: boolean;
  backgroundType: 'audio-video' | 'custom-agent-video' | 'color';
  scheduler?: {
    schedulerType:
      | 'servicebell'
      | 'hubspotMeetings'
      | 'calendly'
      | 'acuity'
      | 'chilipiper';
    servicebellEventType?: number;
  };
  launcherBottomSpacing: number;
  launcherSideSpacing: number;
  buttons: WidgetSettingsOrgButtons;
  sections: WidgetSettingsOrgSections;
  links: WidgetSettingsLink[];
  requireEmailOnChat: WidgetSettingsRequireEmailCondition;
  requireEmailOnChatExcludePersonal: boolean;
  replyTimings: WidgetSettingsReplyTimes;
  inboundRestrictions: {
    allowStartChat: boolean;
    multipleInboundChats: boolean;
    searchBeforeStartingChat: boolean;
    disableRepliesOnClosedChat: boolean;
  };
  gdprOptIn?: boolean;
  gdprOptInPrivacyPolicyUrl?: string;
  gdprOptInTitle?: string;
  gdprOptInBody?: string;
}

export enum OrgOnboardingStep {
  ADD_SERVICEBELL = 'ADD_SERVICEBELL',
  ADD_TEAMMATES = 'ADD_TEAMMATES',
  CREATE_EVENTS = 'CREATE_EVENTS',
  UPLOAD_LOGO = 'UPLOAD_LOGO',
}

export interface OrgOnboarding {
  complete: boolean;
  steps: Record<OrgOnboardingStep, boolean>;
}

export interface OrgProspectPropertiesMeta {
  company: Record<string, OrgProspectPropertyMeta>;
  contact: Record<string, OrgProspectPropertyMeta>;
}

export type OrgProspectPropertyGroup = keyof OrgProspectPropertiesMeta;

// What is stored in Org settings
export interface StoredOrgProspectProperty {
  propertyGroup: OrgProspectPropertyGroup;
  propertyKey: string;
  label: string;
}

export interface OrgProspectPropertyWithMeta {
  propertyKey: string;
  propertyGroup: OrgProspectPropertyGroup;
  propertyName: string;
  label: string;
  type: string;
  isCustom: boolean;
}
export interface OrgProspectPropertyMeta {
  displayName: string;
  type: string;
  isCustom: boolean;
}

export interface BaseOrg {
  id: number;
  status: OrgStatus;
  name?: string;
  clientKey: string;
  enableRecaptcha: boolean;
  enableDevDomains: boolean;
  widgetSettings: OrgWidgetSettings;
  timezone: string;
  hippaMode?: boolean;
  helpcenterOn?: boolean;
  helpcenterTitle: string;
  helpcenterHomeUrl: string;
  helpcenterBackgroundImageUrl: string;
  helpcenterLogoImageUrl: string;
  allowRecording?: boolean;
  autoRecording?: boolean;
  optinRecording?: boolean;
  autoSendProactiveGreeting?: boolean;
  customProactiveGreeting?: string;
  autoSendReactiveGreeting?: boolean;
  customReactiveGreeting?: string;
  requireSecureIdentity: boolean;
  postCallLogging?: boolean;
  phoneCallsOnDials?: boolean;
  phoneCallsOnAlerts?: boolean;
  proactiveCallsOnAlerts?: boolean;
  defaultContactPipelineStageId?: null | number;
  allowAgentsUpdatePipelineStageId?: null | boolean;
  conversationAutoClose?: ConversationAutoCloseSetting;
  onCallOutsideWorkingHoursForbidden?: boolean;
  outboundProspectProperties?: StoredOrgProspectProperty[];
}

export interface OrgWithPlan extends BaseOrg {
  plan: Plan;
}

export interface Org extends OrgWithPlan {
  domains: Domain[];
  users: User[];
  integrations: OrgIntegration[];
  timePreferences: TimePreference[];
}

export interface OrgSettings
  extends Pick<
    Org,
    | 'name'
    | 'enableRecaptcha'
    | 'enableDevDomains'
    | 'timePreferences'
    | 'timezone'
    | 'hippaMode'
    | 'helpcenterTitle'
    | 'helpcenterHomeUrl'
    | 'helpcenterBackgroundImageUrl'
    | 'helpcenterLogoImageUrl'
    | 'helpcenterOn'
    | 'allowRecording'
    | 'autoRecording'
    | 'optinRecording'
    | 'autoSendProactiveGreeting'
    | 'customProactiveGreeting'
    | 'autoSendReactiveGreeting'
    | 'customReactiveGreeting'
    | 'postCallLogging'
    | 'phoneCallsOnDials'
    | 'phoneCallsOnAlerts'
    | 'proactiveCallsOnAlerts'
    | 'defaultContactPipelineStageId'
    | 'allowAgentsUpdatePipelineStageId'
    | 'conversationAutoClose'
    | 'onCallOutsideWorkingHoursForbidden'
    | 'outboundProspectProperties'
  > {
  widgetSettings: Partial<Org['widgetSettings']>;
}

export type OrgPermissions = Record<string, string[]>;

export type OrgUsage = {
  remainingProactiveCalls: null | number;
  remainingReactiveCalls: null | number;
  remainingTakeovers: null | number;
  remainingSpyViewings: null | number;
  remainingSixsenseCompanyDetailFetches: null | number;
};

export interface Account {
  id: number;
  emailAddress: string;
  emailVerified?: boolean;
  users: User[];
}

export enum UserAccounType {
  REGULAR = 'regular',
  PUBLIC = 'public',
}

export interface SelfAccount extends Account {
  type: UserAccounType.REGULAR;
  users: SelfUser[];
  secureIdentityHash: string;
  authedUserOrgId: number | null;
}

export interface SelfPublicAccount extends Account {
  type: UserAccounType.PUBLIC;
  users: SelfUser[];
  authedUserOrgId: number | null;
}

export type SelfAccountOrPublicAccount = SelfAccount | SelfPublicAccount;

export interface SelfAccountWithToken extends SelfAccount {
  authenticationToken?: string;
}

export interface SelfPublicAccountWithToken extends SelfPublicAccount {
  AccessToken?: string;
}

interface BaseUser {
  id: number;
  avatar: { imageUrl: string } | null;
  displayName: string;
  title: string;
  emailAddress: string;
  emailVerified?: boolean;
  orgRoles: OrgRole[];
  onCall: boolean;
}
export interface User extends BaseUser {
  integrations: OtherUserIntegration[];
}

export interface SelfUser extends BaseUser {
  org: Org;
  orgPermissions: OrgPermissions;
  orgUsage: OrgUsage;
  onboarding: UserOnboarding;
  sid: null | string;
  sbidData: null | Client;
  integrations: UserIntegration[];
  gateways: Record<Gateway, boolean>;
  timePreferences: TimePreference[];
  settings: UserSettings;
  phoneNumbers: { number: string }[];
}

export interface SelfPublicUser {
  id: number;
  avatar: { imageUrl: string } | null;
  displayName: string;
  title: string;
  emailAddress: string;
  org: Org;
  sid: null | string;
  settings: UserSettings;
}

export enum Gateway {
  'SEGMENTS' = 'SEGMENTS',
  'CONTEXTUAL_RECORDING' = 'CONTEXTUAL_RECORDING',
  'HELP_CENTER' = 'HELP_CENTER',
  'OUTBOUND' = 'OUTBOUND',
  'PHONE_CALLS' = 'PHONE_CALLS',
  'SALESFLOOR' = 'SALESFLOOR',
  'AI_CHAT' = 'AI_CHAT',
}

export interface UserOnboarding {
  complete: boolean;
  steps: Record<OnboardingStep, boolean>;
}

export interface UserSettings {
  autoDetectAvailability?: boolean;
  autoDetectCalendarAvailability?: boolean;
  videoEffect?: null | VideoEffect;
  videoBackgroundImage?: null | MunchedImage;
  videoBackgroundImageId?: string;
  callbackPhoneNumber?: string;
  calendarEventIntegrationId?: number | null;
  openDealAlerts?: boolean | null;
  outboundCallRecordingEnabled?: boolean;
  outboundCallRecordingRepOnly?: boolean;
  outboundIntlCallRecordingEnabled?: boolean;
  outboundEmailNotificationsEnabled?: boolean;
  outboundCallRecordingIncludedAreaCodes?: string[];
  outboundCallRecordingExcludedAreaCodes?: string[];
  outboundIntlCallRecordingExcludedCountries?: string[];
  outboundDoNotCallList?: string[];
  outboundDialerColumns?: { property: string; enabled: boolean }[];
  outboundAutoOpenSequencerProfiles?: boolean;
}

export interface Credentials {
  emailAddress: string;
  password: string;
  recaptchaToken?: string;
}

export interface OrgSSO {
  orgId: string;
  orgName: string;
  redirectUrl: string;
}

export interface CreateAccountInfo extends Credentials {
  displayName: string;
  goal?: string;
  inviteCode?: string;
}

export interface TeamInvite {
  id: number;
  dateCreated: number;
  address: string;
  accepted: boolean | null;
}

export interface UniqueVisitor {
  id: string;
  customId?: Client['customId'];
  customMetadata?: Client['customMetadata'];
  displayName?: Client['displayName'];
  email?: Client['email'];
  lastSeen?: number;
  contact?: LBBContact;
}

export interface OrgInvite {
  emailAddress: string;
  orgRoles: OrgRole[];
  onCall: boolean;
  code: string;
  org: Org;
  user: User | null;
}

export enum SignUpGoal {
  INCREASE_SALES = 'INCREASE_SALES',
  INCREASE_RETENTION = 'INCREASE_RETENTION',
  IMPROVE_CUSTOMER_SERVICE = 'IMPROVE_CUSTOMER_SERVICE',
  ANALYZE_USERS = 'ANALYZE_USERS',
  INTERCOM_INTEGRATION = 'INTERCOM_INTEGRATION',
  NONE_OF_ABOVE = 'NONE_OF_ABOVE',
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  dateCreated: string;
  dateLastUsed: string;
  active: boolean;
}

export interface UserAvailability {
  id: string;
  isAvailable: boolean;
  isWorkingHours?: boolean;
  isManual: boolean;
  startTime: number;
  endTime: number;
  note: string;
}

export type UserAvailabilityArgs = Omit<
  UserAvailability,
  'id' | 'startTime' | 'isManual'
> &
  Partial<Pick<UserAvailability, 'startTime'>>;
